import {
  addDisplayName,
  addHandlers,
  addProps,
  addState,
  addStateHandlers,
  flowMax,
} from 'ad-hok'
import cx from 'classnames'
import { TFunction } from 'i18next'
import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { addApi } from 'shared/src/api'
import { isLoginError } from 'shared/src/api/types'
import { addNavigate } from 'shared/src/utils/addNavigate'
import { addAuthContext } from 'shared/src/utils/auth'
import { isValidPassword } from 'shared/src/utils/validators'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { sansSerifFontFamily, serifFontFamily } from 'utils/styles'
import { TextField } from '../TextField'
import { TextButton } from './TextButton'

const useStyles = createUseStyles({
  title: {
    color: '#ffffff',
    fontFamily: serifFontFamily,
    fontSize: 32,
    fontWeight: 600,
    lineHeight: '140%',
    letterSpacing: 0.01,
    marginTop: 16,
    marginBottom: 12,
    fontStyle: 'italic',
  },
  subtitle: {
    fontFamily: sansSerifFontFamily,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '187%',
    color: 'rgba(255, 255, 255, 0.56);',
    marginLeft: 30,
    marginBottom: 40,
  },
  passwordRequirements: {
    fontFamily: sansSerifFontFamily,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '160%',
    letterSpacing: 0.2,
    marginTop: 4,
    marginBottom: 18,
    color: 'rgba(255, 255, 255, 0.8)',
  },
  passwordRequirementsError: {
    color: '#F57561',
  },
  button: {
    height: 64,
    width: '100%',
    marginTop: 48,
    background:
      'radial-gradient(103.73% 266.85% at 0% 16.08%, #CF8355 0%, #CEAF85 38.44%, #9B876D 100%)',
    borderRadius: 2,
    color: '#ffffff',
  },
  authErrorText: {
    fontFamily: sansSerifFontFamily,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '160%',
    letterSpacing: 0.2,
    marginTop: 4,
    marginBottom: 18,
    color: '#F57561',
  },
})

const containsError = (password: string, t: TFunction): string | undefined =>
  isValidPassword(password) ? undefined : t('auth.register.invalidPassword')

interface Props {
  email: string
  code?: string
  isUpdate?: boolean
}

export const SetPassword: FC<Props> = flowMax(
  addDisplayName('SetPassword'),
  addProps(() => ({
    classes: useStyles(),
  })),
  addStateHandlers(
    { password: '' },
    {
      onChangePassword: () => (newPassword: string) => ({
        password: newPassword,
      }),
    }
  ),
  addApi,
  addNavigate,
  addState('authError', 'setAuthError', false),
  addAuthContext,
  addHandlers({
    onClick:
      ({ api, email, password, code, navigate, setAuthError, logIn }) =>
      async () => {
        try {
          const response = await api.auth.logIn({
            email,
            password,
            resetPasswordToken: code,
          })

          if (isLoginError(response)) {
            setAuthError(true)
          } else {
            logIn()
            navigate('/', { replace: true })
          }
        } catch (error) {
          console.log(error)
        }
      },
  }),
  addTranslationHelpers,
  addProps(
    ({ password, t }) => ({
      error: password.length === 0 ? undefined : containsError(password, t),
    }),
    ['password', 't']
  ),
  addHandlers({
    onKeyPress:
      ({ onClick, password, error }) =>
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && !(password === '' || !!error)) {
          onClick()
        }
      },
  }),
  ({
    isUpdate,
    password,
    onChangePassword,
    onClick,
    onKeyPress,
    authError,
    error,
    classes,
    t,
  }) => (
    <div>
      <h1 className={classes.title}>
        {t(
          isUpdate
            ? 'auth.updatePassword.title'
            : 'auth.register.createPasswordTitle'
        )}
      </h1>
      <p className={classes.subtitle}>
        {t('auth.register.createPasswordSubtitle')}
      </p>
      {authError && (
        <div className={classes.authErrorText}>
          {t('auth.updatePassword.error')}
        </div>
      )}
      <TextField
        id="password"
        type="password"
        label={t('auth.fields.password')}
        value={password ?? ''}
        onChange={(password) => onChangePassword(password)}
        onKeyPress={onKeyPress}
      />
      <div
        className={cx(
          classes.passwordRequirements,
          error && classes.passwordRequirementsError
        )}
      >
        {t('auth.passwordRequirements')}
      </div>
      <TextButton
        text={t(
          isUpdate
            ? 'auth.updatePassword.button'
            : 'auth.register.setPasswordButton'
        )}
        onClick={onClick}
        disabled={password === '' || !!error}
      />
    </div>
  )
)
